/**
 * Mapper paciente interno → payload mínimo Memed (setPaciente).
 */

import type { PatientDTO } from '@/services/patients';
import type { MemedPatientPayload } from './memed.types';

function digitsOnly(value?: string | null): string | undefined {
  if (!value) return undefined;
  const digits = value.replace(/\D/g, '');
  return digits || undefined;
}

function formatBirthDate(value?: string | Date | null): string | undefined {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    // Já pode estar em DD/MM/YYYY
    if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(value.trim())) {
      return value.trim();
    }
    return undefined;
  }
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = date.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function mapSex(gender?: string | null): 'Masculino' | 'Feminino' {
  const g = String(gender || '').trim().toUpperCase();
  if (g === 'F' || g === 'FEMININO' || g === 'FEMALE') return 'Feminino';
  return 'Masculino';
}

export type MemedPatientSource = Pick<
  PatientDTO,
  'id' | 'cpf' | 'dateOfBirth' | 'gender' | 'phone'
> & {
  user?: { name?: string; email?: string } | null;
  name?: string | null;
};

/**
 * Constrói payload mínimo. Não inclui prontuário, chat, histórico etc.
 */
export function buildMemedPatientPayload(patient: MemedPatientSource): MemedPatientPayload {
  const nome =
    patient.user?.name?.trim() ||
    patient.name?.trim() ||
    'Paciente';

  const payload: MemedPatientPayload = {
    idExterno: String(patient.id),
    nome,
    sexo: mapSex(patient.gender),
  };

  const cpf = digitsOnly(patient.cpf);
  if (cpf) payload.cpf = cpf;

  const birth = formatBirthDate(patient.dateOfBirth);
  if (birth) payload.data_nascimento = birth;

  const phone = digitsOnly(patient.phone);
  if (phone) payload.telefone = phone;

  const email = patient.user?.email?.trim();
  if (email) payload.email = email;

  return payload;
}
